import { useEffect, useState } from 'react'
import { IconCheck, IconLinkOff, IconMinus, IconShield } from '@tabler/icons-react'
import { Link, useNavigate } from 'react-router-dom'

import { authApi } from '@/api/endpoints'
import { errorCode, errorMessage } from '@/api/client'
import type { PasswordLinkInfo } from '@/api/types'
import { LoadingState } from '@/components/common/States'
import { AuthFormBrand, AuthShell, StepsAside, type StepState } from '@/components/auth/AuthShell'
import { STRENGTH, strength } from '@/components/auth/passwordStrength'
import { useAuthStore } from '@/store/authStore'
import { tr } from '@/i18n/tr'
import { Alert, Button, ButtonLink, PasswordField, notifications, useForm } from '@/ui'

/**
 * E-postadaki bağlantıyla parola belirleme: davet ve "parolamı unuttum" aynı ekranı kullanır.
 *
 * <p>Bağlantıdaki değer adresin PARÇA (#) kısmındadır; tarayıcı onu sunucuya göndermez.
 * Okunur okunmaz adres çubuğundan da silinir: tarayıcı geçmişinde ve ekran paylaşımında
 * tek kullanımlık bağlantı durmasın.
 */
export function SetPasswordPage() {
  const navigate = useNavigate()
  const clearSession = useAuthStore((s) => s.clear)
  const [token] = useState(() => window.location.hash.replace(/^#/, '').trim())
  const [info, setInfo] = useState<PasswordLinkInfo | null>(null)
  const [invalid, setInvalid] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname)
    }
    if (!token) {
      setInvalid('Bu sayfa yalnızca e-postanıza gönderilen bağlantıyla açılır.')
      return
    }
    let cancelled = false
    authApi.inspectPasswordLink(token)
      .then((res) => { if (!cancelled) setInfo(res) })
      .catch((err) => {
        if (!cancelled) setInvalid(errorMessage(err, 'Bağlantı doğrulanamadı. Biraz sonra tekrar deneyin.'))
      })
    return () => { cancelled = true }
  }, [token])

  const form = useForm({
    initialValues: { newPassword: '', repeat: '' },
    validate: {
      newPassword: (v) => (v.length >= 10 ? null : tr.auth.passwordTooShort),
      repeat: (v, values) => (v === values.newPassword ? null : tr.auth.passwordMismatch),
    },
  })

  const submit = form.onSubmit(async (values) => {
    setBusy(true)
    setError(null)
    try {
      await authApi.completePasswordLink(token, values.newPassword)
      // Sunucu bu hesabın tüm oturumlarını düşürdü; bu tarayıcıda açık kalan oturum da gitsin.
      clearSession()
      notifications.show({
        color: 'green',
        title: 'Parolanız belirlendi',
        message: 'Yeni parolanızla giriş yapabilirsiniz.',
      })
      navigate('/login', { replace: true })
    } catch (err) {
      // Bağlantı bu arada kullanıldı ya da süresi doldu: form artık işe yaramaz.
      if (errorCode(err) === 'PASSWORD_LINK_INVALID') setInvalid(errorMessage(err))
      else setError(errorMessage(err, 'Parola belirlenemedi.'))
    } finally {
      setBusy(false)
    }
  })

  const invite = info?.purpose === 'INVITE'
  const steps: { label: string; hint: string; state: StepState }[] = [
    {
      label: invite ? 'Davet e-postası' : 'Sıfırlama e-postası',
      hint: invite ? 'Yöneticiniz sizin için bir hesap açtı.' : 'Bağlantıyı e-postanızdan açtınız.',
      state: 'done',
    },
    { label: 'Parolanızı belirleyin', hint: 'En az 10 karakter; tahmin edilmesi zor olsun.', state: 'now' },
    { label: 'Giriş yapın', hint: 'Yeni parolanızla projelerinize devam edin.', state: 'todo' },
  ]

  const { newPassword, repeat } = form.values
  const score = strength(newPassword)
  const rules = [
    { ok: newPassword.length >= 10, label: 'En az 10 karakter' },
    { ok: newPassword.length > 0 && newPassword === repeat, label: 'Parolalar eşleşiyor' },
  ]

  return (
    <AuthShell asideLabel="Adımlar" aside={<StepsAside steps={steps} />}>
      {invalid ? (
        <div className="nx-auth-form nx-auth-form--wide">
          <AuthFormBrand />
          <div className="nx-auth-form__head">
            <h1>Bağlantı kullanılamıyor</h1>
          </div>
          <Alert tone="danger" icon={<IconLinkOff size={18} stroke={1.8} aria-hidden="true" />}>{invalid}</Alert>
          <ButtonLink to="/parolami-unuttum" size="lg" full className="nx-auth-submit">Yeni bağlantı iste</ButtonLink>
          <Link to="/login" className="nx-auth-link">{tr.auth.backToLogin}</Link>
        </div>
      ) : !info ? (
        <LoadingState />
      ) : (
        <form className="nx-auth-form nx-auth-form--wide" onSubmit={submit} noValidate>
          <AuthFormBrand />
          <div className="nx-auth-form__head">
            <h1>{invite ? `Hoş geldiniz, ${info.displayName}` : 'Yeni parolanızı belirleyin'}</h1>
            <p>
              <strong>@{info.username}</strong> hesabı için {invite ? 'bir parola belirleyin.' : 'yeni bir parola belirleyin.'}
            </p>
          </div>

          {error && <Alert tone="danger">{error}</Alert>}

          {/* Parola yöneticisi yeni parolayı doğru hesaba kaydetsin. */}
          <input type="text" name="username" autoComplete="username" value={info.username} readOnly hidden />
          <div className="ui-field">
            <PasswordField label={tr.auth.newPassword} size="lg" autoComplete="new-password" autoFocus
              {...form.getInputProps('newPassword')} />
            <div className="nx-pw-meter" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => <span key={i} style={i < score ? { background: STRENGTH[score].color } : undefined} />)}
            </div>
            <span className="nx-pw-meter__label" aria-live="polite" data-good={score >= 3 ? '' : undefined}>
              Parola gücü: {STRENGTH[score].label}
            </span>
          </div>
          <PasswordField label={tr.auth.newPasswordAgain} size="lg" autoComplete="new-password"
            {...form.getInputProps('repeat')} />

          <ul className="nx-rules">
            {rules.map((r) => (
              <li key={r.label} data-ok={r.ok ? '' : undefined}>
                <span className="nx-rules__icon" aria-hidden="true">
                  {r.ok ? <IconCheck size={11} stroke={3} /> : <IconMinus size={11} stroke={3} />}
                </span>
                <span>{r.label}</span>
                <span className="ui-visually-hidden">{r.ok ? ' (sağlandı)' : ' (sağlanmadı)'}</span>
              </li>
            ))}
          </ul>

          <Button type="submit" size="lg" full loading={busy} className="nx-auth-submit"
            leftIcon={<IconShield size={18} stroke={1.8} aria-hidden="true" />}>
            Parolayı belirle
          </Button>
        </form>
      )}
    </AuthShell>
  )
}
